<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { onClickOutside } from '@vueuse/core';
import format from 'date-fns/format';
import { CSAT_RATINGS } from 'shared/constants/messages';
import { useReportsContext } from '../composables/useReportsContext';
import ReportsPeriodPicker from './ReportsPeriodPicker.vue';

// Spec por tela (padrao ctxSpec do prototipo): o wrapper diz o que a barra
// mostra em cada rota — periodo, chips de escopo, tempo real, HC.
const props = defineProps({
  spec: {
    type: Object,
    default: () => ({ period: true }),
  },
  // Altura da pilha sticky acima (nav simples = 40, nav + subnav = 72)
  stickyTop: {
    type: Number,
    default: 40,
  },
});

const { t } = useI18n();
const store = useStore();
const {
  state,
  range,
  isDefaultPeriod,
  setBusinessHours,
  setScope,
  setGroupBy,
} = useReportsContext();

// ── Período ──
const panelOpen = ref(false);
const panelRef = ref(null);
onClickOutside(panelRef, () => {
  panelOpen.value = false;
});

const periodLabel = computed(() => {
  if (state.periodKey === 'custom') {
    return `${format(range.value.from, 'dd/MM/yy')} – ${format(range.value.to, 'dd/MM/yy')}`;
  }
  return t(`REPORTS_CONTEXT.PRESETS.${state.periodKey.toUpperCase()}`);
});

// Selo ao lado da data: enquanto ninguem mexe, o periodo e o padrao
const periodBadge = computed(() =>
  isDefaultPeriod.value
    ? t('REPORTS_CONTEXT.BADGE_DEFAULT')
    : t('REPORTS_CONTEXT.BADGE_CUSTOM')
);

// ── Agrupar (dia/semana/mes, adaptado ao tamanho do periodo) ──
const groupByOpen = ref(false);
const groupByRef = ref(null);
onClickOutside(groupByRef, () => {
  groupByOpen.value = false;
});

const groupByOptions = computed(() => {
  const days = Math.floor(
    (range.value.to - range.value.from) / (1000 * 60 * 60 * 24)
  );
  const opts = [{ id: 'day', name: t('REPORT.GROUPING_OPTIONS.DAY') }];
  if (days >= 14)
    opts.push({ id: 'week', name: t('REPORT.GROUPING_OPTIONS.WEEK') });
  if (days >= 60)
    opts.push({ id: 'month', name: t('REPORT.GROUPING_OPTIONS.MONTH') });
  if (days >= 364)
    opts.push({ id: 'year', name: t('REPORT.GROUPING_OPTIONS.YEAR') });
  return opts;
});

const groupByLabel = computed(() => {
  const found = groupByOptions.value.find(o => o.id === state.groupBy);
  return found?.name || t('REPORT.GROUPING_OPTIONS.DAY');
});

const pickGroupBy = id => {
  setGroupBy(id);
  groupByOpen.value = false;
};

// ── Escopos ──
const SCOPE_SOURCES = {
  team: { getter: 'teams/getTeams', action: 'teams/get' },
  inbox: { getter: 'inboxes/getInboxes', action: 'inboxes/get' },
  agent: { getter: 'agents/getAgents', action: 'agents/get' },
  label: { getter: 'labels/getLabels', action: 'labels/get' },
  pipeline: { getter: 'pipelines/getAllPipelines', action: 'pipelines/get' },
  sla_policy: { getter: 'sla/getSLA', action: 'sla/get' },
  rating: { static: true },
  // CRM: responsavel sai da lista de agentes; origem e status vem da spec
  owner: { getter: 'agents/getAgents', action: 'agents/get' },
  deal_source: { static: true },
  deal_status: { static: true },
  // Etiqueta DO DEAL: id = titulo (e o que o jsonb deals.labels guarda)
  deal_label: {
    getter: 'labels/getLabels',
    action: 'labels/get',
    idField: 'title',
  },
  product: { getter: 'products/getProducts', action: 'products/get' },
  product_kind: { static: true },
};

const scopeKeys = computed(() => props.spec.scopes || []);

// Funil filtra por tipo de entidade da aba (deal/ticket) via spec
const filterScopeItems = (key, items) => {
  if (key !== 'pipeline') return items;
  const entity = props.spec.pipelineEntity || 'deal';
  if (entity === 'ticket') return items.filter(p => p.entity_type === 'ticket');
  return items.filter(p => !p.entity_type || p.entity_type === 'deal');
};

const openScope = ref(null);
const scopeSearch = ref('');
const scopeRef = ref(null);
onClickOutside(scopeRef, () => {
  openScope.value = null;
});

const toggleScope = key => {
  scopeSearch.value = '';
  openScope.value = openScope.value === key ? null : key;
};

const scopeOptions = computed(() => {
  if (!openScope.value) return [];
  if (openScope.value === 'rating') {
    return CSAT_RATINGS.map(r => ({ id: r.value, name: t(r.translationKey) }));
  }
  if (SCOPE_SOURCES[openScope.value].static) {
    const list = props.spec.staticOptions?.[openScope.value] || [];
    const term = scopeSearch.value.toLowerCase();
    return list.filter(i => !term || i.name.toLowerCase().includes(term));
  }
  const source = SCOPE_SOURCES[openScope.value];
  const items = store.getters[source.getter] || [];
  const q = scopeSearch.value.toLowerCase();
  return filterScopeItems(openScope.value, items)
    .map(i => ({
      id: source.idField ? i[source.idField] : i.id,
      name: i.name || i.title,
    }))
    .filter(i => !q || i.name.toLowerCase().includes(q));
});

const pickScope = (key, item) => {
  setScope(key, item);
  openScope.value = null;
};

const clearScope = key => {
  setScope(key, null);
  openScope.value = null;
};

onMounted(() => {
  scopeKeys.value.forEach(key => {
    const source = SCOPE_SOURCES[key];
    if (source.static) return;
    const items = store.getters[source.getter] || [];
    if (!items.length) store.dispatch(source.action);
  });
});
</script>

<template>
  <div
    class="sticky z-[8] flex items-center justify-center gap-2 px-4 py-[7px] border-b border-n-weak bg-[#EDEBE8] dark:bg-[#171411] flex-wrap"
    :style="{ top: `${stickyTop}px` }"
  >
    <span class="i-lucide-filter size-3.5 text-n-slate-9 flex-none" />

    <!-- Tempo real -->
    <span
      v-if="spec.realtime"
      class="inline-flex items-center gap-1.5 px-[11px] py-1.5 text-xs text-n-slate-11"
    >
      <span class="size-1.5 rounded-full bg-n-teal-9 animate-pulse" />
      {{ t('REPORTS_CONTEXT.REALTIME') }}
    </span>

    <!-- Período -->
    <div v-if="spec.period !== false" ref="panelRef" class="relative">
      <button
        class="inline-flex items-center gap-1.5 px-[11px] py-1.5 text-xs rounded-[9px] border border-n-strong bg-n-solid-2 text-n-slate-11 hover:border-[#BE185D] transition-colors"
        @click="panelOpen = !panelOpen"
      >
        <span class="i-lucide-calendar size-3.5" />
        {{ periodLabel }}
        <span
          class="px-1.5 py-px rounded-full text-[9.5px] font-bold uppercase tracking-wide"
          :class="
            isDefaultPeriod
              ? 'bg-n-alpha-2 text-n-slate-10'
              : 'bg-[#9B2C2C]/10 text-[#9B2C2C] dark:bg-[#F472B6]/15 dark:text-[#F472B6]'
          "
        >
          {{ periodBadge }}
        </span>
        <span class="i-lucide-chevron-down size-3" />
      </button>
      <ReportsPeriodPicker
        v-if="panelOpen"
        class="absolute top-full mt-1.5 ltr:left-0 rtl:right-0 z-50"
        @close="panelOpen = false"
      />
    </div>

    <!-- Agrupar -->
    <div v-if="spec.groupBy" ref="groupByRef" class="relative">
      <button
        class="inline-flex items-center gap-1.5 px-[11px] py-1.5 text-xs rounded-[9px] border border-n-strong bg-n-solid-2 text-n-slate-11 hover:border-[#BE185D] transition-colors"
        @click="groupByOpen = !groupByOpen"
      >
        {{ t('REPORTS_CONTEXT.GROUP_BY') }}: {{ groupByLabel }}
        <span class="i-lucide-chevron-down size-3" />
      </button>
      <div
        v-if="groupByOpen"
        class="absolute top-full mt-1.5 ltr:left-0 rtl:right-0 z-50 flex flex-col p-1.5 rounded-xl border border-n-weak bg-n-solid-2 shadow-xl min-w-[140px]"
      >
        <button
          v-for="opt in groupByOptions"
          :key="opt.id"
          class="px-2.5 py-1.5 text-xs rounded-lg text-left hover:bg-n-alpha-1"
          :class="
            state.groupBy === opt.id
              ? 'text-[#9B2C2C] dark:text-[#F472B6] font-semibold'
              : 'text-n-slate-11'
          "
          @click="pickGroupBy(opt.id)"
        >
          {{ opt.name }}
        </button>
      </div>
    </div>

    <!-- Separador antes dos escopos -->
    <span
      v-if="scopeKeys.length"
      class="w-px h-4 bg-n-strong opacity-40 flex-none"
    />

    <!-- Escopos: ghost vazio ("Time ▾") / ativo rose ("Time: X ×") -->
    <div v-if="scopeKeys.length" ref="scopeRef" class="flex items-center gap-2">
      <div v-for="key in scopeKeys" :key="key" class="relative">
        <button
          v-if="state.scopes[key]"
          class="inline-flex items-center gap-1.5 px-[11px] py-1.5 text-xs rounded-[9px] border border-[#BE185D] bg-[#9B2C2C]/10 dark:bg-[#F472B6]/15 text-[#9B2C2C] dark:text-[#F472B6] font-medium"
          @click="toggleScope(key)"
        >
          {{ t(`REPORTS_CONTEXT.SCOPES.${key.toUpperCase()}`) }}:
          {{ state.scopes[key].name }}
          <span
            class="i-lucide-x size-3 hover:opacity-70"
            @click.stop="clearScope(key)"
          />
        </button>
        <button
          v-else
          class="inline-flex items-center gap-1.5 px-[11px] py-1.5 text-xs rounded-[9px] border border-dashed border-n-strong bg-transparent text-n-slate-10 hover:border-[#BE185D] hover:border-solid hover:text-n-slate-11 transition-colors"
          @click="toggleScope(key)"
        >
          {{ t(`REPORTS_CONTEXT.SCOPES.${key.toUpperCase()}`) }}
          <span class="i-lucide-chevron-down size-3" />
        </button>
        <div
          v-if="openScope === key"
          class="absolute top-full mt-1.5 ltr:left-0 rtl:right-0 z-50 flex flex-col p-1.5 rounded-xl border border-n-weak bg-n-solid-2 shadow-xl min-w-[220px] max-h-80 overflow-y-auto"
        >
          <input
            v-model="scopeSearch"
            type="text"
            :placeholder="t('REPORTS_CONTEXT.SEARCH')"
            class="mb-1 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-xs px-2 py-1.5 flex-none"
          />
          <button
            class="px-2.5 py-1.5 text-xs rounded-lg text-left text-n-slate-10 hover:bg-n-alpha-1 flex-none"
            @click="clearScope(key)"
          >
            {{ t('REPORTS_CONTEXT.ALL') }}
          </button>
          <button
            v-for="item in scopeOptions"
            :key="item.id"
            class="px-2.5 py-1.5 text-xs rounded-lg text-left truncate hover:bg-n-alpha-1 flex-none"
            :class="
              state.scopes[key]?.id === item.id
                ? 'text-[#9B2C2C] dark:text-[#F472B6] font-semibold'
                : 'text-n-slate-11'
            "
            @click="pickScope(key, item)"
          >
            {{ item.name }}
          </button>
        </div>
      </div>
    </div>

    <!-- Horário comercial -->
    <button
      v-if="spec.businessHours"
      class="inline-flex items-center gap-2 px-[11px] py-1.5 text-xs rounded-[9px] border border-dashed transition-colors"
      :class="
        state.businessHours
          ? 'border-green-500 text-n-slate-12'
          : 'border-n-strong text-n-slate-10 hover:border-[#BE185D]'
      "
      @click="setBusinessHours(!state.businessHours)"
    >
      <span
        class="relative inline-block w-[26px] h-[15px] rounded-full transition-colors"
        :class="state.businessHours ? 'bg-green-500' : 'bg-n-slate-5'"
      >
        <span
          class="absolute top-[2px] w-[11px] h-[11px] rounded-full bg-white shadow transition-all"
          :class="state.businessHours ? 'left-[13px]' : 'left-[2px]'"
        />
      </span>
      {{ t('REPORTS_CONTEXT.BUSINESS_HOURS') }}
    </button>
  </div>
</template>
